import {
    Button, ChangeEvent, ClickEvent, DataHeader, Dialog, DropdownItem, DropdownSelectionEvent, Layout, LookupModelSearchEvent,
    LookupModelSelectionEvent, TableCell, TableRow, TableRowMode, Textbox
} from "@mcleod/components";
import { Api, McLeodClassicIntegration, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutCarrierQualificationProfile } from "./autogen/AutogenLayoutCarrierQualificationProfile";
import { CommonDialogs } from "@mcleod/common";
import { CarrierQualProfileValueSelectorPopup, ISelectorFields } from "./CarrierQualProfileValueSelectorPopup";
import { DuplicateId, DuplicateIdProps } from "./DuplicateId";

const viewCarrierQualProfileURL = "lme/dispatch/CarrierQualificationProfile?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "brk_qual_profile",
    screenName: "Carrier Qualification Profile",
    navigateToUrl: viewCarrierQualProfileURL
};

export class CarrierQualificationProfile extends AutogenLayoutCarrierQualificationProfile {

    valueSelectorDialog: Dialog;

    override onLoad(): void {
        this.switchActive.required = false;
        
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showDelete = true;
    }

    async tableCarrierQualDetailsOnRowDisplay(event) {
        const tableRow = event.getTableRow();
        const textboxQualificationId1 = tableRow.findComponentById("textboxQualificationId1") as Textbox;
        const textboxQualificationId2 = tableRow.findComponentById("textboxQualificationId2") as Textbox;
        const textboxAdditionalProfile = tableRow.findComponentById("textboxProfile") as Textbox;
        const textboxComparison2 = tableRow.findComponentById("textboxComparison2");
        const textboxPointsType = tableRow.findComponentById("textboxPointsType");
        const textboxPoints = tableRow.findComponentById("textboxPoints") as Textbox;
        const textboxDetailType = tableRow.findComponentById("textboxDetailType");
        const textboxValue1 = tableRow.findComponentById("textboxValue1");
        const textboxValue2 = tableRow.findComponentById("textboxValue2");
        const textboxItem1ComparisonType = tableRow.findComponentById("textboxItem1comparisonType");
        const textboxItem2ComparisonType = tableRow.findComponentById("textboxItem2comparisonType");

        const textboxValue1Display:Textbox = tableRow.findComponentById("textboxValue1Display") as Textbox;
        const textboxValue2Display:Textbox = tableRow.findComponentById("textboxValue2Display") as Textbox;

        textboxItem1ComparisonType.required = false;
        textboxItem2ComparisonType.required = false;
        textboxValue1.required = false;
        textboxValue1Display.required = false;
        textboxValue2.required = false;
        textboxValue2Display.required = false;
        textboxPointsType.required = true;

        if (textboxDetailType.selectedItem?.value == 'S')
        {
            textboxQualificationId2.required = false;
            textboxComparison2.required = false;
            textboxValue2.required = false
            textboxValue2Display.required = false;
            textboxItem2ComparisonType.required = false;

            textboxQualificationId2.visible = false;
            textboxComparison2.visible = false;
            textboxValue2.visible = false;
            textboxValue2Display.visible = false;
            textboxItem2ComparisonType.visible = false;
        }

        if (tableRow.mode != TableRowMode.SEARCH) {
            await this.setupComparisonAndValueDropdowns(textboxQualificationId1.boundRow.getFirstLookupModelData("qualification_id")?.get("id"),
                textboxQualificationId2.boundRow.getFirstLookupModelData("qualification_id2")?.get("id"), tableRow, event);
            this.choosePointsType(tableRow);
        }
        else if (tableRow.mode == TableRowMode.QUICK_ADD)
        {
            textboxPoints.required = true;
            textboxAdditionalProfile.enabled = false;
            textboxValue1Display.visible = false;
            textboxValue2Display.visible = false;
        }
        else
        {
            textboxAdditionalProfile.enabled = false;
        }
    }

    textboxDetailTypeOnDropdownSelection(event: DropdownSelectionEvent) {
        if (!event.hasSelection())
            return;

        const tableRow = TableRow.getContainingTableRow(event.target);
        const textboxQualificationId2 = tableRow.findComponentById("textboxQualificationId2");
        const textboxComparison2 = tableRow.findComponentById("textboxComparison2");
        const textboxValue2 = tableRow.findComponentById("textboxValue2");
        const textboxValue2Display = tableRow.findComponentById("textboxValue2Display");
        const buttonValue2Selector = tableRow.findComponentById("buttonValue2Selector") as Button
        const textboxItem2ComparisonType = tableRow.findComponentById("textboxItem2comparisonType");

        const isComboType:boolean = event.selection?.value != 'S';
        textboxQualificationId2.required = isComboType;
        textboxComparison2.required = isComboType;
        textboxQualificationId2.visible = isComboType;
        textboxComparison2.visible = isComboType;

        const valComparison2Type:string = textboxItem2ComparisonType.valueAsString;
        const comp2TypeBlank:boolean = StringUtil.isEmptyString(valComparison2Type);
        const comp2TypeConstrained:boolean = (valComparison2Type === "C");

        textboxValue2.enabled = isComboType && (comp2TypeBlank || (!comp2TypeBlank && comp2TypeConstrained))
        textboxValue2.visible = isComboType && (comp2TypeBlank || (!comp2TypeBlank && comp2TypeConstrained))
        buttonValue2Selector.visible = isComboType && !textboxValue2.visible;
        textboxValue2Display.visible = isComboType && !textboxValue2.visible;
    }

    async textboxQualificationIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const newQualId = event.target.boundRow.getFirstLookupModelData("qualification_id")?.get("id");
            await this.setupComparisonAndValueDropdowns(newQualId, null, tableRow, event);
        }
    }

    async textboxQualificationId2OnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            const newQualId = event.target.boundRow.getFirstLookupModelData("qualification_id2")?.get("id");
            await this.setupComparisonAndValueDropdowns(null, newQualId, tableRow, event);
        }
    }

    textboxProfileBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    async setupComparisonAndValueDropdowns(qualId1: string, qualId2: string, tableRow: TableRow, event)
    {
        const textboxComparison1 = tableRow.findComponentById("textboxComparison1") as Textbox;
        const textboxComparison2 = tableRow.findComponentById("textboxComparison2") as Textbox;
        const textboxValue1 = tableRow.findComponentById("textboxValue1") as Textbox;
        const textboxValue1Display = tableRow.findComponentById("textboxValue1Display") as Textbox;
        const textboxValue2 = tableRow.findComponentById("textboxValue2") as Textbox;
        const textboxValue2Display = tableRow.findComponentById("textboxValue2Display") as Textbox;
        const buttonValue1Selector = tableRow.findComponentById("buttonValue1Selector") as Button
        const buttonValue2Selector = tableRow.findComponentById("buttonValue2Selector") as Button

        await Api.post("lme/dispatch/brk-qual-item-display", {
            brk_qual_item_id: qualId1,
            brk_qual_item_id2: qualId2
        }).then((response) => {
            if (qualId1) {
                const compValues = response?.data[0].comp_values;
                const compResult: DropdownItem[] = [];
                for (const dbDisplayValue of compValues) {
                    const dropdownItem = new DropdownItem(dbDisplayValue.value, dbDisplayValue.displayValue);
                    compResult.push(dropdownItem);
                }
                textboxComparison1.items = compResult;
                textboxComparison1.setSelectedItemFromValue(textboxComparison1.getDataValue());
                if (textboxComparison1.selectedItem == null)
                    tableRow.data.set("comparison", null);

                const valValues = response?.data[0].val_values;
                const valDisplay = response?.data[0].val_display;
                const valDisabled = response?.data[0].val_disabled as boolean;

                const valComparison1Type = response?.data[0].comparison1_type as String;
                const valComparison2Type = response?.data[0].comparison2_type as String;

                const comp1TypeBlank:boolean = StringUtil.isEmptyString(valComparison1Type);
                const comp2TypeBlank:boolean = StringUtil.isEmptyString(valComparison2Type);
                const comp1TypeConstrained:boolean = (valComparison1Type === "C" || valComparison1Type === "E");
                const comp2TypeConstrained:boolean = (valComparison2Type === "C" || valComparison2Type === "E");

                textboxValue1.enabled = comp1TypeBlank || !comp1TypeConstrained;
                textboxValue1.visible = comp1TypeBlank || !comp1TypeConstrained;
                textboxValue1Display.visible = comp1TypeConstrained;
                buttonValue1Selector.visible = comp1TypeConstrained;

                const textboxDetailType = tableRow.findComponentById("textboxDetailType") as Textbox;
                const isComboType:boolean = textboxDetailType.valueAsString != "S";
                textboxValue2.enabled = isComboType && (comp2TypeBlank || (!comp2TypeBlank && !comp2TypeConstrained))
                textboxValue2.visible = isComboType && (comp2TypeBlank || (!comp2TypeBlank && !comp2TypeConstrained))
                buttonValue2Selector.visible = isComboType && !textboxValue2.visible;
                textboxValue2Display.visible = isComboType && !textboxValue2.visible;

                if (valDisabled)
                {
                    textboxValue1._displayType = valDisplay;
                    textboxValue1.items = valValues;
                    textboxValue1._setText(valValues as string, event);
                    textboxValue1.items = null;
                    textboxValue1Display.items = valValues;

                    if (!StringUtil.isEmptyString(event.target?.data?.get("tooltip_1", ""))) {
                        textboxValue1Display.valueAsString = StringUtil.replaceAll(event.target?.data?.get("tooltip_1", ""), "\n", ", ");
                    }
                    textboxValue1Display.items = null;

                }
                else if (valDisplay != null && !valDisplay.isEmptyString)
                {
                    textboxValue1.items = null;
                    textboxValue1._displayType = valDisplay;
                    textboxValue1Display.items = null;
                }
                else
                {
                    textboxValue1._displayType = null;
                    textboxValue1.enabled = false;

                    textboxValue1Display._displayType = null;
                    textboxValue1Display.enabled = false;
                    let result: string = textboxValue1.getBasicValue();
                    if ((result as String).substring(0,2) == ",,") {
                        result = "<Blank>," + result.substring(2,result.length);
                    }

                    if (!StringUtil.isEmptyString(event.target?.data?.get("tooltip_1", ""))) {
                        textboxValue1Display.valueAsString = StringUtil.replaceAll(event.target?.data?.get("tooltip_1", ""), "\n", ", ");
                    }

                    Api.search("lme/dispatch/brk-qual-detail", {multiselect:true, brk_qual_item_id:qualId1,
                    saved_values:textboxValue1.getBasicValue() })
                }
            }
            if (qualId2)
            {
                const comp2Values = response?.data[0].comp_values2;
                const comp2Result: DropdownItem[] = [];
                for (const dbDisplayValue of comp2Values) {
                    const dropdownItem = new DropdownItem(dbDisplayValue.value, dbDisplayValue.displayValue);
                    comp2Result.push(dropdownItem);

                }
                textboxComparison2.items = comp2Result;
                textboxComparison2.setSelectedItemFromValue(textboxComparison2.getDataValue());
                if (textboxComparison2.selectedItem == null)
                    tableRow.data.set("comparison2", null);

                const valValues2 = response?.data[0].val_values2;
                const valDisplay2 = response?.data[0].val_display2;
                const valDisabled2 = response?.data[0].val_disabled2 as boolean;
                textboxValue2.enabled = !valDisabled2;

                if (valDisabled2)
                {
                    textboxValue2._displayType = valDisplay2;
                    textboxValue2.items = valValues2;
                    textboxValue2._setText(valValues2 as string, event);
                    textboxValue2.items = null;

                    textboxValue2Display._displayType = valDisplay2;
                    textboxValue2Display.items = valValues2;
                    if (!StringUtil.isEmptyString(event.target?.data?.get("tooltip_2", ""))) {
                        textboxValue2Display.valueAsString = StringUtil.replaceAll(event.target?.data?.get("tooltip_2", ""), "\n", ", ");
                    }
                    textboxValue2Display.items = null;
                }
                else if (valDisplay2 != null && !valDisplay2.isEmptyString)
                {
                    textboxValue2.items = null;
                    textboxValue2._displayType = valDisplay2;
                }
                else
                {
                    textboxValue2._displayType = null;
                    textboxValue2.enabled = false;

                    textboxValue2Display._displayType = null;
                    textboxValue2Display.enabled = false;

                    let result: string = textboxValue2.getBasicValue();
                    if ((result as String).substring(0,2) == ",,") {
                        result = "<Blank>," + result.substring(2,result.length);
                    }
                    if (!StringUtil.isEmptyString(event.target?.data?.get("tooltip_2", ""))) {
                        textboxValue2Display.valueAsString = StringUtil.replaceAll(event.target?.data?.get("tooltip_2", ""), "\n", ", ");
                    }

                    Api.search("lme/dispatch/brk-qual-detail", {multiselect:true, brk_qual_item_id:qualId2,
                        saved_values:textboxValue2.getBasicValue()})
                }
            }
        })
    }

    private async showValueSelector(selectedValues: ISelectorFields, qualificationId: String): Promise<ISelectorFields> {
        // This method assembles the results and sends the string of values
        const props = { height: 460, width: 355, title: "Value Selection" };
        const layout = Layout.getLayout("lme/dispatch/CarrierQualProfileValueSelectorPopup") as CarrierQualProfileValueSelectorPopup;
        layout.initControls(selectedValues, qualificationId);
        const dialog = await CommonDialogs.showDialog(layout, props);
        if (dialog.wasCancelled !== true) {
            const result: ISelectorFields = layout.getResult() as ISelectorFields;
            if (result !== undefined)
                return result;
            return null;
        }
        return selectedValues;
    }

    async selectValues(tableRow:TableRow, textboxQualificationId:Textbox, textboxValue: Textbox, textboxDisplay: Textbox) {
        if (tableRow.mode != TableRowMode.QUICK_ADD){
            const curBoundRow = textboxValue.boundRow as ModelRow;
            const tableRowData = curBoundRow.data;
            const qualificationId = tableRowData.qualification_id;
            const startingValues: ISelectorFields = {idValues: textboxValue.text, descriptions: textboxDisplay.valueAsString };
            const selectedResult: ISelectorFields = await this.showValueSelector(startingValues, qualificationId);
            textboxValue.boundRow.set(textboxValue.field, selectedResult.idValues);
            let descriptionValue = StringUtil.replaceAll(selectedResult.descriptions, "\n", ", ");
            if (descriptionValue.substring(descriptionValue.length-2, descriptionValue.length) === ", ") {
              descriptionValue = descriptionValue.substring(0, descriptionValue.length-2);
            }
            textboxDisplay.boundRow.set(textboxDisplay.field, descriptionValue);
          }
          else {

            const startingValues: ISelectorFields = {idValues: "", descriptions: "" };
            const qualificationId = textboxQualificationId.getRelevantModelRow().get("qualification_id");

            const selectedResult: ISelectorFields = await this.showValueSelector(startingValues, qualificationId);
            textboxValue.valueAsString = StringUtil.replaceAll(selectedResult.idValues, "<Blank>", ",");
            let descriptionValue = StringUtil.replaceAll(selectedResult.descriptions, "\n", ", ");
            if (descriptionValue.substring(descriptionValue.length-2, descriptionValue.length) === ", ") {
                descriptionValue = descriptionValue.substring(0, descriptionValue.length-2);
            }
            textboxDisplay.text = descriptionValue;
          }
    }

    async buttonValue1SelectorOnClick(event: ClickEvent) {
        const clickedButton = event.target as Button;
        const tableCell = clickedButton.parent as TableCell;
        const tableRow = tableCell.row;
        const textboxValue1 = tableCell.findComponentById("textboxValue1") as Textbox;
        const textboxValue1Display = tableCell.findComponentById("textboxValue1Display") as Textbox;
        const textboxQualificationId = tableRow.findComponentById("textboxQualificationId1") as Textbox;

        this.selectValues(tableRow, textboxQualificationId, textboxValue1, textboxValue1Display);

    }

    async buttonValue2SelectorOnClick(event: ClickEvent) {
        const clickedButton = event.target as Button;
        const tableCell = clickedButton.parent as TableCell;
        const tableRow = tableCell.row;
        const textboxValue2 = tableCell.findComponentById("textboxValue2") as Textbox;
        const textboxValue2Display = tableCell.findComponentById("textboxValue2Display") as Textbox;
        const textboxQualificationId = tableRow.findComponentById("textboxQualificationId2") as Textbox;

        this.selectValues(tableRow, textboxQualificationId, textboxValue2, textboxValue2Display);

    }

    textboxPointsTypeOnDropdownSelection(event)
    {
        const tableRow = TableRow.getContainingTableRow(event.target);
        this.choosePointsType(tableRow);
    }

    choosePointsType(tableRow:TableRow)
    {
        const textboxPointsType = tableRow.findComponentById("textboxPointsType") as Textbox;
        const textboxAdditionalProfile = tableRow.findComponentById("textboxProfile") as Textbox;
        const textboxPoints = tableRow.findComponentById("textboxPoints") as Textbox;
        if (textboxPointsType.selectedItem?.value == 'P')
        {
            textboxAdditionalProfile.clear();
            textboxAdditionalProfile.required = false;
            textboxAdditionalProfile.enabled = false;
            textboxPoints.required = true;
            textboxPoints.enabled = true;
        }
        else
        {
            textboxPoints.clear();
            textboxPoints.required = false;
            textboxPoints.enabled = false;
            textboxAdditionalProfile.required = true;
            textboxAdditionalProfile.enabled = true;
        }
    }

    textboxQualificationId1OnChange(event: ChangeEvent)
    {
        const textboxQualificationId = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textboxQualificationId);
        const textboxValue1 = tableRow.findComponentById("textboxValue1") as Textbox;
        const textboxValue1Display = tableRow.findComponentById("textboxValue1Display") as Textbox;
        const buttonValueSelector = tableRow.findComponentById("buttonValue1Selector") as Button;

        const valComparisonType = textboxQualificationId.boundRow.getFirstLookupModelData("qualification_id")?.get("comparison_type");
        const compTypeBlank:boolean = StringUtil.isEmptyString(valComparisonType);
        const compTypeConstrained:boolean = (valComparisonType === "C" || valComparisonType === "E");
        textboxValue1.enabled = (compTypeBlank || (!compTypeBlank && !compTypeConstrained))
        textboxValue1.visible = (compTypeBlank || (!compTypeBlank && !compTypeConstrained))
        buttonValueSelector.visible = compTypeConstrained;
        textboxValue1Display.visible = compTypeConstrained;
        if (event.oldValue != event.newValue)
        {
            textboxValue1.clear();
            textboxValue1Display.clear();
        }
    }

    textboxQualificationId2OnChange(event: ChangeEvent)
    {
        const textboxQualificationId = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textboxQualificationId);
        const textboxDetailType = tableRow.findComponentById("textboxDetailType") as Textbox;
        const textboxValue2 = tableRow.findComponentById("textboxValue2") as Textbox;
        const textboxValue2Display = tableRow.findComponentById("textboxValue2Display") as Textbox;
        const buttonValue2Selector = tableRow.findComponentById("buttonValue2Selector") as Button;
        const isComboType:boolean = textboxDetailType.valueAsString != "S";

        const valComparison2Type = textboxQualificationId.boundRow.getFirstLookupModelData("qualification_id2")?.get("comparison_type");
        const comp2TypeBlank:boolean = StringUtil.isEmptyString(valComparison2Type);
        const comp2TypeConstrained:boolean = (valComparison2Type === "C");
        textboxValue2.enabled = isComboType && (comp2TypeBlank || (!comp2TypeBlank && !comp2TypeConstrained))
        textboxValue2.visible = isComboType && (comp2TypeBlank || (!comp2TypeBlank && !comp2TypeConstrained))
        buttonValue2Selector.visible = isComboType && !textboxValue2.visible;
        textboxValue2Display.visible = isComboType && !textboxValue2.visible;

        if (event.oldValue != event.newValue)
        {
            textboxValue2.clear();
            textboxValue2Display.clear();
        }
    }

    beforeLookupModelSearchValue1(event: LookupModelSearchEvent) {
        event.filter.multiselect = true;
        const tableRow = TableRow.getContainingTableRow(event.target);
        const textboxQualificationId1 = tableRow.findComponentById("textboxQualificationId1");
        const qualId1 = textboxQualificationId1.boundRow.getFirstLookupModelData("qualification_id")?.get("id");
        event.filter.brk_qual_item_id = qualId1;
    }

    beforeLookupModelSearchValue2(event: LookupModelSearchEvent) {
        event.filter.multiselect = true;
        const tableRow = TableRow.getContainingTableRow(event.target);
        const textboxQualificationId2 = tableRow.findComponentById("textboxQualificationId2");
        const qualId2 = textboxQualificationId2.boundRow.getFirstLookupModelData("qualification_id2")?.get("id");
        event.filter.brk_qual_item_id = qualId2;
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.brdsp.EntryBrkQualProfile", this.mainDataSource.activeRow.get("id"));
    }
 }
